import { Link, Outlet } from '@tanstack/react-router'
import { Logo } from './logo.js'
import { Button } from './ui/button.js'
import { t } from '../i18n/index.js'
import { useAuth } from '../lib/auth.js'

/**
 * Application shell for signed-in users.
 *
 * The Nutzer entry only appears for admins — but that is presentation, not
 * protection: the API rejects a capturer on those routes regardless of what the
 * navigation shows.
 */
export function Layout() {
  const strings = t()
  const { user, signOut, hasRole } = useAuth()

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-border bg-surface-raised border-b">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-3">
          <Link to="/status" className="shrink-0" aria-label={strings.app.title}>
            <Logo className="text-base" />
          </Link>

          <nav className="flex gap-1" aria-label={strings.app.title}>
            {hasRole('admin') && (
              <Link
                to="/stammdaten"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.valueLists}
              </Link>
            )}
            {hasRole('platform') && (
              <Link
                to="/mandanten"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.tenants}
              </Link>
            )}
            {hasRole('admin') && (
              <Link
                to="/messen"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.events}
              </Link>
            )}
            <Link
              to="/artikel"
              className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
              activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
            >
              {strings.nav.items}
            </Link>
            {hasRole('admin') && (
              <Link
                to="/felder"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.attributes}
              </Link>
            )}
            <Link
              to="/export"
              className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
              activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
            >
              {strings.nav.exports}
            </Link>
            {hasRole('admin') && (
              <Link
                to="/zu-pruefen"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.pending}
              </Link>
            )}
            {hasRole('admin') && (
              <Link
                to="/users"
                className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
                activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
              >
                {strings.nav.users}
              </Link>
            )}
            <Link
              to="/status"
              className="hover:bg-surface-sunken rounded-md px-3 py-1.5 text-sm"
              activeProps={{ className: 'bg-accent-subtle text-accent font-medium' }}
            >
              {strings.nav.status}
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-text-muted hidden text-sm sm:inline">
              {strings.nav.signedInAs} <strong className="font-medium">{user?.name}</strong>
            </span>
            <Button variant="secondary" size="sm" onClick={() => void signOut()}>
              {strings.nav.signOut}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <Outlet />
      </main>
    </div>
  )
}
